import { ACTION_NAMES, Texture } from '../../../Constants';
import {
    angryDocks,
    doIHaveTheMaximumStaffAvailableToMe,
    totalStaffCount,
    whichStackHasGhostActions,
    whichStackHasStaffActions, whichStacksHaveThisAction
} from '../../Game/GamePlayerInfo';
import {
    available_docks_for_guest,
    CARD_FACTIONS,
    get_points_for_card
} from '../../TwoD/Common/CardUtilities';
import {
    activateARandomActionRoom,
    availableRandomActions,
    canIActivateARandomActionRoom,
    canIActivateThisRoom,
    matchingActionRooms
} from '../../TwoD/Common/StationUtilities';
import {
    currentActionGrid,
    isRandomActionTexture
} from '../../TwoD/Common/ActionHexUtilities';


function _activateActionRoom(gamePlayerController, aiPackage, action) {
    let stack = null;
    if (!gamePlayerController.gamePlayerData.locked_action_stacks[0]) {
        if (gamePlayerController.gamePlayerData.available_actions[0].includes(action)) {
            stack = 0;
        }
    }

    if (!gamePlayerController.gamePlayerData.locked_action_stacks[1]) {
        if (gamePlayerController.gamePlayerData.available_actions[1].includes(action)) {
            stack = 1;
        }
    }
    aiPackage.addProvenance(
        `Are the stacks locked?`,
        stack != null,
        `If a stack isn't locked activate the matching action room.`);

    if (stack != null) {
        const roomsForStack0 = matchingActionRooms(gamePlayerController.gamePlayerData, action);
        const activatedRooms = roomsForStack0.filter(roomData => roomData.activated);
        const deActivatedRooms = roomsForStack0.filter(roomData => !roomData.activated);
        aiPackage.addProvenance(
            `Is there a Matching Action Room?`,
            roomsForStack0.length > 0,
            'Get more actions for the primary action.');
        if (roomsForStack0.length > 0) {
            aiPackage.addProvenance(
                `Is the first ${ACTION_NAMES[action]} Room Activated?`,
                activatedRooms.length !== 0,
                'Get more actions for the primary action.');
            if (activatedRooms.length === 0) {
                const room = deActivatedRooms.pop();
                const canI = canIActivateThisRoom(gamePlayerController.gamePlayerData,
                    room.stationIndex);
                aiPackage.addProvenance(
                    `Can I activate ${room.name}?`,
                    canI,
                    'Activate room for more actions.');
                if (canI) {
                    aiPackage.addToMessage(`Activate ${room.name}`);
                    if (aiPackage.execute) {
                        gamePlayerController.gamePlayerApi.toggleActivation(room.stationIndex);
                    }
                    return true;
                }
            }
        }
    }
    return false;
}


export default function artificialSelectAction(gamePlayerController, aiPackage) {
    // if (_activatePrimaryRoom(gamePlayerController, aiPackage)) return;

    // CC_FEAI_SELECTION_REDRIVE_GATE (prod G156, 2026-07-14): a selection click
    // is legal ONLY while the engine still has this seat owing a selection.
    // Between our click leaving and the engine's answering patch arriving, the
    // FE snapshot still reads SELECT_ACTION — so ANY other inbound patch in that
    // window fires a normal diff-gate drive, this deterministic tree re-runs on
    // unchanged data, and it re-emits an IDENTICAL selection. By then the engine
    // has advanced the seat (select_action -> execute_action sets turn_status
    // EXECUTE_ACTION, the action router sets status RESEARCHING / RENOVATING),
    // and it dispatches by CURRENT status: the duplicate lands in
    // research_action.route's texture branch -> add() -> room_id = data.room,
    // which an ActionGrid selection click structurally never carries -> the
    // 'Invalid Research Add Target' / 'Invalid Renovation Package' Broadcast
    // Errors of G156.
    //
    // The fence records that this seat has already committed a selection for
    // this episode. It is NOT a one-shot latch: ArtificialPlayer.updateEvent
    // clears it off the seat's LIVE turn_status every eval (a real engine
    // boundary, per take_turn.select_next_action / execute_action), so it
    // self-heals across the game boundary — the hazard of
    // [[project_feai_autoexecute_stuck_false_across_games]]. ArtificialPlayer
    // .rejectEvent clears it too, because a REJECTED selection rearms the seat
    // with turn_status still SELECT_ACTION; without that clear the reject-retry
    // would be fenced out and the seat would strand.
    //
    // This is NOT the send-seam drop CC_AUTOPLAY_DEDUP_SILENT_DROP forbids. No
    // click is silently dropped: the seat's FIRST selection always reaches the
    // engine and is guaranteed to answer with a patch or a typed Reject, so the
    // seat cannot strand and always-act is satisfied by the original click
    // already in flight. What is suppressed is only the redundant re-emit of a
    // decision we have already put on the wire.
    if (gamePlayerController._selectionCommitDone) {
        aiPackage.addProvenance(
            'Have I already selected an action for this turn step?',
            true,
            'The engine already has my selection; never send it twice.');
        aiPackage.addToMessage('Waiting for my selection to land');
        // Mark success so this does NOT fall through to the turn-over branch
        // below: a clickNext at SELECT_ACTION is end_turn (take_turn.next_button),
        // so declining here would end the turn on a stale re-drive.
        aiPackage.success = true;
        return;
    }

    let actionGrid = currentActionGrid(gamePlayerController.gamePlayerData);
    // Is there a staff action? if i am full of staff, take the other action first.
    // if I am ghosted, take the ghost action first.

    let doStaffFirst = false;
    let hasStaff = whichStackHasStaffActions(gamePlayerController.gamePlayerData);
    if (hasStaff != null) {
        doStaffFirst = !doIHaveTheMaximumStaffAvailableToMe(gamePlayerController.gamePlayerData);
    }
    let hasGhostAction = whichStackHasGhostActions(gamePlayerController.gamePlayerData) != null;

    if (actionGrid.length > 0) {
        let question = 'What is the first action available to me?';
        let reason = 'First is easiest.';

        let actionRoomTexture, actionGridIndex;
        if (doStaffFirst) {
            question = 'Staff First.';
            reason = 'I am not full of staff, so I want them to be available.'
            actionRoomTexture = Texture.ROBOT_ACTION;
        } else if (hasGhostAction && angryDocks(gamePlayerController.gamePlayerData).length > 1) {
            question = 'Ghost First.';
            reason = 'Always deal with Angry ghosts before anything else.'
            actionRoomTexture = Texture.GHOST_ACTION;
        } else {
            actionRoomTexture = actionGrid[0];
        }
        // The forced staff/ghost-first texture (ROBOT_ACTION / GHOST_ACTION)
        // may not actually be present in this turn's grid; indexOf would then
        // be -1 and selectActionStack(-1, ...) reads server-side as a negative
        // Python index (the LAST grid hex). Fall back to the normal first-
        // available action so a real, present hex is always selected.
        if (actionGrid.indexOf(actionRoomTexture) === -1) {
            actionRoomTexture = actionGrid[0];
        }
        let roomName = Texture.NAMES[actionRoomTexture];
        actionGridIndex = actionGrid.indexOf(actionRoomTexture);
        let action = Texture.ACTIONS.indexOf(actionRoomTexture);

        // A Random Action selector (texture 83) is not an ordinary main-hex
        // action: the engine routes it to start_random_action, which Rejects
        // (no_available_random_action) when the seat's live
        // available_random_actions is empty. The grid tile can be stale vs the
        // engine's randoms_used_this_round (pipelined FE state — the race the
        // backend documents), so re-derive availability at click time and
        // suppress the selection when none is actually available, exactly
        // mirroring the backend guard. Without this the FE-AI emitted an
        // illegal selectActionStack(_, 83, _) that reject-stormed the seat
        // (G147, 2026-07-14). When a random IS available the existing
        // selectActionStack path drives it (unchanged).
        if (isRandomActionTexture(actionRoomTexture) &&
                availableRandomActions(
                    gamePlayerController.gamePlayerData).length === 0) {
            aiPackage.addProvenance(
                'Is a Random Action actually available right now?',
                false,
                'Never emit a random-action click the engine will reject.');
        } else {
            if (_activateActionRoom(gamePlayerController, aiPackage, action)) return;
            aiPackage.addToMessage(`Select ${roomName}`);
            aiPackage.addProvenance(question, roomName, reason);

            if (aiPackage.execute) {
                const actionId = Texture.ACTIONS.indexOf(actionRoomTexture);
                const stackData = whichStacksHaveThisAction(
                    gamePlayerController.gamePlayerData,
                    actionId)
                let stack = null;
                if (actionId < 7) {
                    stack = 0;
                    if (!stackData.stacks[0]) stack = 1;
                }
                gamePlayerController.gamePlayerApi.selectActionStack(
                    actionGridIndex, actionRoomTexture, stack);
                // CC_FEAI_SELECTION_REDRIVE_GATE: this seat now owes the engine
                // nothing further for this selection episode — the click is on
                // the wire and will answer with a patch or a typed Reject. Fence
                // any re-drive that reads the still-stale SELECT_ACTION snapshot
                // until updateEvent observes the seat leave SELECT_ACTION (or
                // rejectEvent clears it for a retry). Set ONLY on the execute
                // pass: the suggest pass emitted nothing, so it must not fence.
                gamePlayerController._selectionCommitDone = true;
            }

            aiPackage.success = true;
        }

    }

    if (!aiPackage.success) {
        const question = 'Can I activate a Random Action Room?';
        const answer = canIActivateARandomActionRoom(gamePlayerController.gamePlayerData);
        const reason = 'Take every action possible.';
        if (!aiPackage.success && answer) {
            aiPackage.addToMessage(`Activate Random Action Room`);
            aiPackage.addProvenance(question, true, reason);
            if (aiPackage.execute) {
                activateARandomActionRoom(gamePlayerController);
            }
            aiPackage.success = true;
        }
    }


    if (!aiPackage.success) {
        aiPackage.addToMessage(`The turn is over`);
        aiPackage.addProvenance(
            'Is there anything else to do?',
            false,
            'I always hope for more.');
        if (aiPackage.execute) {
            console.log('AI NO MORE ACTIONS, CLICKING DONE.')
            gamePlayerController.gamePlayerApi.clickNext();
        }
    }

    aiPackage.success = true;
}
